<template>
  <VaSidebar
    v-model="writableVisible"
    :width="sidebarWidth"
    :color="color"
    :minimized-width="minimizedWidth"
    :minimized="isMinimized"
    :animated="true"
  >
    <!-- Collapsed Sidebar - Show only icons -->
    <template v-if="isMinimized">
      <div class="collapsed-sidebar">
        <VaSidebarItem
          v-for="(route, index) in filteredRoutes"
          :key="index"
          :to="route.children ? undefined : { name: route.name }"
          :active="routeHasActiveChild(route)"
          :active-color="activeColor"
          :text-color="textColor(route)"
          :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${navT(route.displayName)}`"
          role="button"
          hover-opacity="0.10"
          class="collapsed-item"
        >
          <VaSidebarItemContent class="py-3 px-2 flex justify-center">
            <VaIcon
              v-if="route.meta.icon"
              aria-hidden="true"
              :name="route.meta.icon"
              size="24px"
              :color="iconColor(route)"
            />
          </VaSidebarItemContent>
          <!-- Tooltip for collapsed items -->
          <VaTooltip :message="navT(route.displayName)" placement="right" :disabled="!isMinimized" />
        </VaSidebarItem>
      </div>
    </template>

    <!-- Expanded Sidebar - Show full navigation -->
    <template v-else>
      <VaAccordion v-model="value" multiple>
        <VaCollapse v-for="(route, index) in filteredRoutes" :key="index">
          <template #header="{ value: isCollapsed }">
            <VaSidebarItem
              :to="route.children ? undefined : { name: route.name }"
              :active="routeHasActiveChild(route)"
              :active-color="activeColor"
              :text-color="textColor(route)"
              :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${navT(route.displayName)}`"
              role="button"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-4">
                <VaIcon
                  v-if="route.meta.icon"
                  aria-hidden="true"
                  :name="route.meta.icon"
                  size="20px"
                  :color="iconColor(route)"
                />
                <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                  {{ navT(route.displayName) }}
                  <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </template>
          <template #body>
            <template v-for="(childRoute, index2) in route.children" :key="index2">
              <!-- Child with its own submenu (e.g. Docs > Main, System) -->
              <div v-if="childRoute.children?.length" class="sidebar-nested-group">
                <div class="sidebar-nested-label py-2 pr-2 pl-11 flex items-center gap-2 text-secondary">
                  <VaIcon v-if="childRoute.meta?.icon" :name="childRoute.meta.icon" size="18px" />
                  <span class="font-semibold text-sm">{{ navT(childRoute.displayName) }}</span>
                </div>
                <div v-for="(grandchild, index3) in childRoute.children" :key="index3" class="sidebar-nested-item">
                  <VaSidebarItem
                    :to="{ name: grandchild.name }"
                    :active="isActiveChildRoute(grandchild)"
                    :active-color="activeColor"
                    :text-color="textColor(grandchild)"
                    :aria-label="`Visit ${navT(grandchild.displayName)}`"
                    hover-opacity="0.10"
                  >
                    <VaSidebarItemContent class="py-2 pr-2 pl-14">
                      <VaSidebarItemTitle class="leading-5 font-semibold text-sm">
                        {{ navT(grandchild.displayName) }}
                      </VaSidebarItemTitle>
                    </VaSidebarItemContent>
                  </VaSidebarItem>
                </div>
              </div>
              <!-- Child without submenu (single link) -->
              <div v-else>
                <VaSidebarItem
                  :to="{ name: childRoute.name }"
                  :active="isActiveChildRoute(childRoute)"
                  :active-color="activeColor"
                  :text-color="textColor(childRoute)"
                  :aria-label="`Visit ${navT(childRoute.displayName)}`"
                  hover-opacity="0.10"
                >
                  <VaSidebarItemContent class="py-3 pr-2 pl-11">
                    <VaSidebarItemTitle class="leading-5 font-semibold">
                      {{ navT(childRoute.displayName) }}
                    </VaSidebarItemTitle>
                  </VaSidebarItemContent>
                </VaSidebarItem>
              </div>
            </template>
          </template>
        </VaCollapse>
      </VaAccordion>
    </template>
  </VaSidebar>
</template>

<script lang="ts">
import { defineComponent, watch, ref, computed, onUnmounted, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import { getNavigationRoutes, type UserRole } from '@/services/navigationService'
export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
    minimized: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t, te } = useI18n()

    const navT = (key: string) => (te(key) ? t(key) : key)

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.name === section.name || route.path.endsWith(`${section.name}`)
      }
      const hasActive = (child: INavigationRoute) =>
        route.name === child.name ||
        route.path.endsWith(`${child.name}`) ||
        (child.children?.some((c) => hasActive(c)) ?? false)
      return section.children.some(hasActive)
    }

    const setActiveExpand = () =>
      (value.value = navigationRoutes.routes.map((route: INavigationRoute) => routeHasActiveChild(route)))

    const isMinimized = computed(() => props.minimized)
    const minimizedWidth = computed(() => '64px')
    const sidebarWidth = computed(() => {
      if (props.mobile) return '100vw'
      if (isMinimized.value) return minimizedWidth.value
      return '280px'
    })
    const color = computed(() => getColor('#051436'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : '#ffffff')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    // Get user data from localStorage reactively
    const userData = ref(() => {
      try {
        const storedData = localStorage.getItem('userData')
        return storedData ? JSON.parse(storedData) : {}
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error)
        return {}
      }
    })

    // Watch for changes in localStorage and update userData
    const updateUserData = () => {
      try {
        const storedData = localStorage.getItem('userData')
        if (storedData) {
          userData.value = JSON.parse(storedData)
        } else {
          userData.value = {}
        }
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error)
        userData.value = {}
      }
    }

    // Listen for storage events (when localStorage changes in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'userData') {
        updateUserData()
      }
    }

    const handleUserDataUpdate = () => {
      updateUserData()
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('userDataUpdated', handleUserDataUpdate)

    // Cleanup event listeners on component unmount
    onUnmounted(() => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('userDataUpdated', handleUserDataUpdate)
    })

    // Helper function to filter routes by role
    // Helper function to get user role
    const getUserRole = () => {
      // First check stored role
      const storedRole = localStorage.getItem('userRole')
      if (storedRole) {
        return storedRole
      }

      // Fallback to userData analysis
      if (userData.value.manager === 1 || userData.value.admin === 1 || userData.value.role === 'org_admin') {
        return 'admin'
      }
      if (userData.value.role === 'recruiter' || userData.value.recruiter === 1) {
        return 'recruiter'
      }
      if (userData.value.role === 'finance' || userData.value.role === 'finance-staff') {
        return 'finance'
      }
      if (userData.value.role === 'support_agent') {
        return 'support_agent'
      }
      if (userData.value.role === 'analyst_viewer_org') {
        return 'analyst_viewer_org'
      }
      if (userData.value.role === 'guest') {
        return 'guest'
      }
      if (userData.value.role === 'team_manager') {
        return 'team_manager'
      }

      return 'operator' // Default fallback
    }

    const filteredRoutes = computed(() => {
      const userRole = getUserRole()

      console.log('Current user role:', userRole)
      console.log('User data:', userData.value)

      // Admin-style roles should always see the full main console sidebar
      if (userRole === 'admin' || userRole === 'manager' || userRole === 'org_admin') {
        console.log('Showing main admin sidebar routes')
        return navigationRoutes.routes
      }

      const routesConfig = getNavigationRoutes(userRole as UserRole)
      console.log('Using navigation config for role:', userRole)
      return routesConfig.routes
    })

    // Initialize user data on mount
    onMounted(() => {
      updateUserData()
      console.log('AppSidebar mounted, user role:', getUserRole())
    })

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    // Watch for changes in userData to update routes
    watch(
      userData,
      () => {
        console.log('User data changed, updating routes')
      },
      { deep: true },
    )

    return {
      writableVisible,
      sidebarWidth,
      minimizedWidth,
      isMinimized,
      value,
      color,
      activeColor,
      filteredRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      navT,
      iconColor,
      textColor,
      arrowDirection,
    }
  },
})
</script>

<style scoped>
/* Override any uppercase styling for sidebar text */
.va-sidebar-item-title {
  text-transform: capitalize !important;
}

/* Ensure all sidebar text is capitalized */
.va-sidebar-item-content {
  text-transform: capitalize !important;
}

/* Override any global uppercase styles for sidebar */
.va-sidebar .va-sidebar-item-title {
  text-transform: capitalize !important;
}

.va-sidebar .va-sidebar-item-content {
  text-transform: capitalize !important;
}

/* Collapsed sidebar styles */
.collapsed-sidebar {
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0;
}

.collapsed-item {
  margin: 0.25rem 0;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
}

.collapsed-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  transform: scale(1.05);
}

.collapsed-item .va-sidebar-item-content {
  padding: 0.75rem 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Tooltip styles for collapsed items */
.va-tooltip {
  z-index: 1000;
}

/* Smooth transitions for sidebar collapse/expand */
.va-sidebar {
  transition: width 0.3s ease;
}

/* Ensure proper spacing in collapsed mode */
.va-sidebar[data-minimized='true'] {
  min-width: 64px;
  max-width: 64px;
}
</style>
